import { create } from 'zustand';
import { message } from 'antd';
import {
    getGroupV3,
    updateGroupV3,
    deleteGroupV3,
    archiveGroupV3,
    getGroupContactsV3,
    addGroupContactsV3,
    removeGroupContactsV3
} from '../reducers/group/resources';
import { searchContact } from '../reducers/contact/resources';
import session from '../utils/session';

const useGroupDetailStore = create((set, get) => ({
    // Group data
    group: null,
    loading: false,
    
    // Contacts data
    contacts: [],
    contactsLoading: false,
    pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
    },

    // Sort (server-side)
    sort: {
        field: 'name', // one of: name, status, notes, created_at
        order: 'asc'
    },
    
    // Edit state
    isEditing: false,
    editedName: '',
    editedPrivacy: '',
    
    // Modals
    addContactsModalOpen: false,
    deleteModalOpen: false,
    
    // Add contacts search
    searchResults: [],
    searchLoading: false,
    selectedContactIds: [],
    searchQuery: '',

    // Actions
    setIsEditing: (editing) => {
        const group = get().group;
        set({ 
            isEditing: editing,
            editedName: editing && group ? group.name : '',
            editedPrivacy: editing && group ? group.privacy : ''
        });
    },

    setEditedName: (name) => set({ editedName: name }),
    setEditedPrivacy: (privacy) => set({ editedPrivacy: privacy }),

    setAddContactsModalOpen: (open) => {
        set({ 
            addContactsModalOpen: open,
            searchResults: [],
            selectedContactIds: [],
            searchQuery: ''
        });
    },

    setDeleteModalOpen: (open) => set({ deleteModalOpen: open }),

    setSelectedContactIds: (ids) => set({ selectedContactIds: ids }),

    setSort: (field, order) => set({ sort: { field, order } }),

    fetchGroup: async (groupId) => {
        set({ loading: true });
        try {
            const res = await getGroupV3(groupId);
            const groupData = res.data.data || res.data;
            set({ 
                group: groupData, 
                loading: false,
                editedName: groupData.name,
                editedPrivacy: groupData.privacy
            });
            return groupData;
        } catch (error) {
            console.error('Failed to fetch group:', error);
            set({ loading: false });
            message.error(error.response?.data?.message || 'Failed to load group');
            throw error;
        }
    },

    fetchContacts: async (groupId, page = 1, pageSize = 20, search = '') => {
        set({ contactsLoading: true });
        try {
            const { sort } = get();
            const res = await getGroupContactsV3(groupId, {
                page,
                pageSize,
                search,
                sort: sort.field,
                order: sort.order
            });
            const data = res.data.data || res.data;
            set({ 
                contacts: data.rows || [],
                pagination: {
                    page: data.page || 1,
                    pageSize: data.pageSize || 20,
                    total: data.total || 0,
                    totalPages: data.totalPages || 0
                },
                contactsLoading: false
            });
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
            set({ contactsLoading: false });
            message.error('Failed to load contacts');
        }
    },

    updateGroup: async (groupId) => {
        const { editedName, editedPrivacy, group } = get();
        set({ loading: true });
        try {
            const payload = {};
            if (editedName !== group.name) {
                payload.name = editedName;
            }
            if (editedPrivacy !== group.privacy && group.isOwner) {
                payload.privacy = editedPrivacy;
            }

            if (Object.keys(payload).length === 0) {
                set({ isEditing: false, loading: false });
                return;
            }

            await updateGroupV3(groupId, payload);
            await get().fetchGroup(groupId);
            set({ isEditing: false, loading: false });
            message.success('Group updated successfully');
        } catch (error) {
            console.error('Failed to update group:', error);
            set({ loading: false });
            message.error(error.response?.data?.message || 'Failed to update group');
        }
    },

    deleteGroup: async (groupId, onSuccess) => {
        set({ loading: true });
        try {
            await deleteGroupV3(groupId);
            set({ loading: false, deleteModalOpen: false });
            message.success('Group deleted successfully');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Failed to delete group:', error);
            set({ loading: false });
            message.error(error.response?.data?.message || 'Failed to delete group');
        }
    },

    archiveGroup: async (groupId, onSuccess) => {
        set({ loading: true });
        try {
            await archiveGroupV3(groupId);
            set({ loading: false, deleteModalOpen: false });
            message.success('Group archived successfully');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Failed to archive group:', error);
            set({ loading: false });
            message.error(error.response?.data?.message || 'Failed to archive group');
        }
    },

    removeContact: async (groupId, contactId) => {
        try {
            await removeGroupContactsV3(groupId, [contactId]);
            const { pagination } = get();
            await get().fetchContacts(groupId, pagination.page, pagination.pageSize);
            message.success('Contact removed from group');
        } catch (error) {
            console.error('Failed to remove contact:', error);
            message.error('Failed to remove contact');
        }
    },

    searchContacts: async (query) => {
        set({ searchLoading: true, searchQuery: query });
        try {
            const { contacts } = get();
            const existingIds = contacts.map(c => c.id);
            
            const res = await searchContact({
                searchKey: query,
                groupContacts: JSON.stringify(contacts),
                pagination: JSON.stringify({ numberOfRows: 50, page: 1 }),
                filter: 'name'
            });
            
            const results = res.data.rows || [];
            // Filter out contacts already in the group
            const filtered = results.filter(c => !existingIds.includes(c.id));
            set({ searchResults: filtered, searchLoading: false });
        } catch (error) {
            console.error('Failed to search contacts:', error);
            set({ searchResults: [], searchLoading: false });
        }
    },

    addSelectedContacts: async (groupId) => {
        const { selectedContactIds, pagination } = get();
        if (selectedContactIds.length === 0) {
            message.warning('No contacts selected');
            return;
        }

        set({ searchLoading: true });
        try {
            await addGroupContactsV3(groupId, selectedContactIds);
            await get().fetchContacts(groupId, pagination.page, pagination.pageSize);
            set({ 
                addContactsModalOpen: false, 
                searchLoading: false,
                selectedContactIds: [],
                searchResults: []
            });
            message.success('Contacts added successfully');
        } catch (error) {
            console.error('Failed to add contacts:', error);
            set({ searchLoading: false });
            message.error('Failed to add contacts');
        }
    },

    applyGroupMembersChanged: ({ groupId, contactCount }) => {
        const { group, pagination } = get();
        if (!group || group.id !== groupId) return;
        // Patch the contact count on the open group, and refetch the visible page
        // so newly added rows appear / removed rows disappear without a manual reload.
        set({ group: { ...group, contact_count: contactCount } });
        get().fetchContacts(groupId, pagination.page, pagination.pageSize);
    },

    // Reset store
    reset: () => set({
        group: null,
        loading: false,
        contacts: [],
        contactsLoading: false,
        pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
        sort: { field: 'name', order: 'asc' },
        isEditing: false,
        editedName: '',
        editedPrivacy: '',
        addContactsModalOpen: false,
        deleteModalOpen: false,
        searchResults: [],
        searchLoading: false,
        selectedContactIds: [],
        searchQuery: ''
    })
}));

export default useGroupDetailStore;
